import React, { useState, useEffect, useMemo, useCallback, forwardRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Icon, Loader } from '@atoms';
import { TextInput } from '@atoms/Input';
import { LocationDropdown } from '@molecules';
import { Flex, Typography } from '@mixins';
import { useDebounce } from '@hooks';
import { LOADING } from '@constants/requestPhase';
import { theme } from '@styles';
import { getLocations } from '@store/location/duck';

import { ReactComponent as Close } from '@assets/svgs/Close.svg';
import { ReactComponent as Search } from '@assets/svgs/Search.svg';
import { ReactComponent as NoLocations } from '@assets/svgs/NoLocations.svg';

import { arrayOf, bool, func, number } from 'prop-types';
import { ItemTitle, ScrollableContainer } from './styles';

const LocationList = forwardRef(
  (
    {
      selectedLocationId,
      isOpen,
      onOpenStatusChange,
      onLocationSelect,
      topPosition,
      leftPosition,
      globalAccessAvailable,
    },
    ref,
  ) => {
    const { locations, getLocationsPhase } = useSelector(store => store.locationStore);

    const [searchInput, setSearchInput] = useState('');

    const debouncedInput = useDebounce(searchInput, 300);
    const dispatch = useDispatch();

    const onSearchChange = useCallback(event => {
      setSearchInput(event.target.value);
    }, []);

    useEffect(() => {
      if (isOpen) {
        dispatch(getLocations({ search: debouncedInput, ...(globalAccessAvailable && { globalAccessAvailable }) }));
      }
    }, [debouncedInput, isOpen]);

    const items = useMemo(
      () =>
        locations.map(country => (
          <LocationDropdown key={country.name} title={country.name} showTitle={!debouncedInput}>
            <Flex alignSelf="start" px={!debouncedInput ? 20 : 0} flexDirection="column">
              {country.cities.map((city, index) => (
                <Flex key={city.name} flexDirection="column" mt={index || !debouncedInput ? 20 : 0}>
                  <Typography variant="diatype-500" color={theme.color.gray[200]}>
                    {city.name}
                  </Typography>
                  <Flex pl={15} flexDirection="column">
                    {city.locations.map(({ id, name }) => (
                      <ItemTitle
                        key={id}
                        onClick={onLocationSelect(id)}
                        variant={selectedLocationId === id ? 'diatype-500' : 'diatype-400'}
                        mt={16}
                        pointer>
                        {name}
                      </ItemTitle>
                    ))}
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </LocationDropdown>
        )),
      [locations, selectedLocationId],
    );

    return (
      <Flex
        ref={ref}
        zIndex={9}
        flexDirection="column"
        position="absolute"
        top={topPosition}
        left={leftPosition}
        width={[300, 390]}
        height={[330, 335, 340]}
        backgroundColor={theme.color.white}
        boxShadow="0px 2px 2px rgba(0, 0, 0, 0.25)"
        isExpanded={isOpen}>
        <Icon
          pointer
          top={[23, 23, 27, 31]}
          right={17}
          position="absolute"
          SVG={Close}
          width={[9, 10]}
          height={[9, 10]}
          onClick={onOpenStatusChange}
        />
        <Flex borderRadius="4px 4px 0px 0px" p="16px 43px 16px 20px" backgroundColor="#EFECE6">
          <TextInput placeholder="Search Locations..." onChange={onSearchChange} value={searchInput}>
            <Icon mr={9} color={theme.color.black} pointer SVG={Search} width={18} height={18} />
          </TextInput>
        </Flex>
        {getLocationsPhase === LOADING ? (
          <Loader background="none" width={50} height={50} position="absolute" />
        ) : (
          <ScrollableContainer m="6px" px={15} pt={15}>
            {items.length ? (
              items
            ) : (
              <Flex pt={60} flexDirection="column" height="100%" alignItems="center" justifyContent="center">
                <Icon SVG={NoLocations} width={35} height={42} color={theme.color.mintMinded[900]} mb={30} />
                <Typography variant="diatype-400">No results</Typography>
              </Flex>
            )}
          </ScrollableContainer>
        )}
      </Flex>
    );
  },
);

LocationList.propTypes = {
  selectedLocationId: number,
  topPosition: arrayOf(number),
  leftPosition: arrayOf(number),
  onLocationSelect: func.isRequired,
  isOpen: bool.isRequired,
  onOpenStatusChange: func.isRequired,
  globalAccessAvailable: bool,
};

LocationList.defaultProps = {
  globalAccessAvailable: null,
  selectedLocationId: null,
  topPosition: [0],
  leftPosition: [0],
};

export default LocationList;
